import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, interval, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { AgentLeadNotificationService } from 'src/app/services/agent-lead-notification.service';
import { MessageServiceService } from 'src/app/services/message-service.service';
import { AcceptAgentResponse, PendingAgentSession, UserChatHistoryApiResponse } from 'src/app/models/agent-lead-notification.model';

const POLL_INTERVAL_MS = 2000;
const NOTIFICATION_SOUND_PATH = 'assets/audio/agent-notification.wav';

const DECLINE_SNOOZE_MS = 5000;

@Component({
  selector: 'app-agent-lead-notification',
  templateUrl: './agent-lead-notification.component.html',
  styleUrls: ['./agent-lead-notification.component.scss']
})
export class AgentLeadNotificationComponent implements OnInit, OnDestroy {
  pendingCards: PendingAgentSession[] = [];
  selectedChatData: UserChatHistoryApiResponse | null = null;
  selectedAgentId = 0;
  showChatDetail = false;
  acceptingSessionId: string | null = null;

  private declinedSessionExpiry = new Map<string, number>();
  private acceptedSessionIds = new Set<string>();
  private pollSubscription: Subscription | null = null;
  private audioUnlocked = false;
  private soundPending = false;
  private unlockHandler: () => void = null;
  private notificationAudio: HTMLAudioElement | null = null;

  constructor(
    private agentLeadNotificationService: AgentLeadNotificationService,
    private messageService: MessageServiceService
  ) {}

  ngOnInit(): void {
    this.initAudio();
    if (this.isAgentUser()) {
      this.startPolling();
    }
  }

  ngOnDestroy(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
    }
    document.removeEventListener('click', this.unlockHandler);
    document.removeEventListener('keydown', this.unlockHandler);
    document.removeEventListener('touchstart', this.unlockHandler);
  }

  private initAudio(): void {
    this.notificationAudio = new Audio(NOTIFICATION_SOUND_PATH);
    this.notificationAudio.preload = 'auto';
    this.notificationAudio.load();

    this.unlockHandler = () => {
      this.audioUnlocked = true;
      document.removeEventListener('click', this.unlockHandler);
      document.removeEventListener('keydown', this.unlockHandler);
      document.removeEventListener('touchstart', this.unlockHandler);

      if (this.soundPending) {
     
        this.soundPending = false;
        this.playNotificationSound();
        return;
      }

     
      const audio = this.notificationAudio;
      if (audio) {
        const unlockPromise = audio.play();
        if (unlockPromise) {
          unlockPromise
            .then(() => {
              audio.pause();
              audio.currentTime = 0;
            })
            .catch(() => { /* will retry on next gesture */ });
        }
      }
    };
    document.addEventListener('click', this.unlockHandler);
    document.addEventListener('keydown', this.unlockHandler);
    document.addEventListener('touchstart', this.unlockHandler);
  }

  private isAgentUser(): boolean {
    const currentUser: any = JSON.parse(localStorage.getItem('LOGGED_USER_INFO'));
    return !!(currentUser && currentUser.roleName && currentUser.roleName.toLowerCase().includes('agent'));
  }

  private getAgentId(): number {
    return +localStorage.getItem('LOGGED_USER_CRMID') || 0;
  }

  private startPolling(): void {
    this.pollSubscription = interval(POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.agentLeadNotificationService.getPendingAgentRequests().pipe(
            catchError(err => {
              console.error('Error polling pending agent requests:', err);
              return of(null);
            })
          )
        )
      )
      .subscribe((res) => this.handlePollResult(res));
  }

  private handlePollResult(session: PendingAgentSession | null): void {
    // null means the poll request itself failed (or there's no pending request) — don't wipe existing cards over a network blip.
    if (!session) {
      return;
    }

    const now = Date.now();
    for (const [sessionId, expiry] of this.declinedSessionExpiry) {
      if (expiry <= now) {
        this.declinedSessionExpiry.delete(sessionId);
      }
    }

    const liveSessionId = session.session_id || null;

    // Drop any card that's no longer the live pending session — another agent
    // may have already accepted it (or it timed out) since our last poll.
    // Skip the card we're actively in the middle of accepting ourselves.
    this.pendingCards = this.pendingCards.filter(
      c => c.session_id === liveSessionId || this.acceptingSessionId === c.session_id
    );

    if (!liveSessionId) {
      return;
    }

    const knownSessionIds = new Set([
      ...this.pendingCards.map(c => c.session_id),
      ...this.declinedSessionExpiry.keys(),
      ...this.acceptedSessionIds
    ]);
    if (!knownSessionIds.has(liveSessionId)) {
      this.pendingCards = [...this.pendingCards, session];
      this.playNotificationSound();
    }
  }

  private playNotificationSound(): void {
    if (!this.audioUnlocked || !this.notificationAudio) {
      this.soundPending = true;
      return;
    }
    const audio = this.notificationAudio;
    audio.pause();
    audio.currentTime = 0;
    audio.play().catch(err => console.error('Unable to play notification sound:', err));
  }

  accept(card: PendingAgentSession): void {
    if (this.acceptingSessionId) { return; }
    this.acceptingSessionId = card.session_id;
    const agentId = this.getAgentId();

    // Remove card immediately and mark as accepted — prevent polling from re-adding it
    this.acceptedSessionIds.add(card.session_id);
    this.removeCard(card.session_id);

    // Call acceptAgent API and use isAgentRequestAccepted to decide flow
    this.agentLeadNotificationService.acceptAgent({
      Phone_number: card.patient_mobile || '',
      Session_id: card.session_id,
      Agent_id: agentId
    }).subscribe(
      (res: AcceptAgentResponse) => {
        this.acceptingSessionId = null;

        if (res && res.isAgentRequestAccepted) {
          // This agent successfully accepted — fetch chat history and open popup
          this.agentLeadNotificationService
            .getUserChatHistory({
              Session_Id: card.session_id,
              MobileNumber: card.patient_mobile || ''
            })
            .subscribe(
              (chatRes) => {
                this.selectedChatData = chatRes;
                this.selectedAgentId = agentId;
                this.showChatDetail = true;
              },
              (_chatError) => {
                this.messageService.showErrorMessage('Unable to fetch lead chat history.');
              }
            );
        } else {
          // Another agent accepted first — card already removed, let this agent know why
          this.messageService.showErrorMessage(
            (res && res.message) || 'This request has already been accepted by another agent.'
          );
        }
      },
      (_error) => {
        this.acceptingSessionId = null;
        if (_error && _error.status === 502) {
          this.messageService.showErrorMessage('Unable to connect to whatsapp. Please try again later.');
          return;
        }
        if (_error && _error.status === 409) {
          // Backend rejects with 409 when another agent already accepted this session
          const body = _error.error;
          this.messageService.showErrorMessage(
            (body && body.message) || 'This request has already been accepted by another agent.'
          );
          return;
        }
        this.messageService.showErrorMessage('Unable to accept lead request.');
      }
    );
  }

  decline(card: PendingAgentSession): void {
    this.declinedSessionExpiry.set(card.session_id, Date.now() + DECLINE_SNOOZE_MS);
    if (this.acceptingSessionId === card.session_id) {
      this.acceptingSessionId = null;
    }
    this.removeCard(card.session_id);
  }

  closeChatDetail(): void {
    this.showChatDetail = false;
    this.selectedChatData = null;
    this.selectedAgentId = 0;
  }

  private removeCard(sessionId: string): void {
    this.pendingCards = this.pendingCards.filter(c => c.session_id !== sessionId);
  }
}
